/**
 * 竖排文本布局（M3-16 / DESIGN.md §2.5、§6.3）。
 *
 * 与横排（layout.ts）共用禁则表与压缩/悬挂退让规则，坐标约定改为：
 * **行从右到左排列，行内字符从上到下**（JLReq 的竖排惯例）。
 *
 * 三类流单元：
 * - CJK/标点：整段先按竖排方向 shaping 一次，拿到 vert/vrt2 替换后的
 *   glyph（HarfBuzz 在 buffer 方向为 TTB 时自动请求这两个特性，见
 *   shaper.ts 的说明），逐字符一个单元，纵向 advance = 一个字身（fontSize）。
 * - 拉丁/数字连续串（长度 >2，或含字母）：整串旋转 90°（顺时针），
 *   沿行方向的跨度 = 横排 shaping 得到的总宽度。
 * - 两位以内的纯数字串：纵中横（tate-chu-yoko）——不旋转，横向居中排列
 *   在一个字身宽度内，纵向只占一个字身。
 *
 * 已知简化（后续任务）：纵向标点挤压（横排 layout.ts 有的"连续约物second
 * 减半"）在此未实现——竖排标点本身已经是全角字身，视觉冗余没有横排那么
 * 明显，优先级排在禁则/旋转/纵中横之后（§2.5 的实现顺序原话）。
 */
import { isCjk, isLatinOrDigit, resolveKinsokuOverflow, KINSOKU_LINE_END, KINSOKU_LINE_START } from './layout';
import type { Shaper } from './shaper';

const ASCENT_RATIO = 0.88;

export interface VerticalGlyph {
  readonly glyphId: number;
  /** 行内字符下标（原始文本，供 undo 一致性校验）。 */
  readonly cluster: number;
  /** 文本框局部空间，行的中心线偏移。 */
  readonly x: number;
  /** 沿行方向（从上到下）的位置。 */
  readonly y: number;
  /** 顺时针旋转角度。0 = 不转（CJK/纵中横），90 = 拉丁串整体转向。 */
  readonly rotate: 0 | 90;
}

export interface VerticalLine {
  readonly start: number;
  readonly end: number;
  /** 本行沿行方向的总跨度（像素）。 */
  readonly extent: number;
}

export interface VerticalLayout {
  readonly glyphs: readonly VerticalGlyph[];
  readonly lines: readonly VerticalLine[];
  /** 全部行横向跨度之和（文本框宽度）。 */
  readonly width: number;
  /** 单行最大纵向跨度（文本框高度）。 */
  readonly height: number;
}

export interface VerticalLayoutOptions {
  readonly fontSize: number;
  /** 行间距倍数（相对字号）。 */
  readonly lineHeight: number;
  /** 容器高度；省略 = 点文本，不折行。 */
  readonly maxHeight?: number;
}

type UnitKind = 'cjk' | 'tcy' | 'latinRun';

interface FlowUnit {
  readonly kind: UnitKind;
  readonly text: string;
  readonly startIndex: number;
}

/** 纯数字、长度 1–2 → 纵中横；其余拉丁/数字串整体旋转。 */
function classifyLatinRun(text: string): 'tcy' | 'latinRun' {
  return /^[0-9]{1,2}$/.test(text) ? 'tcy' : 'latinRun';
}

/** 把文本切成流单元：CJK/标点逐字符一个单元，拉丁/数字连续串合并。 */
function toFlowUnits(text: string): FlowUnit[] {
  const chars = [...text];
  const units: FlowUnit[] = [];
  let i = 0;
  while (i < chars.length) {
    const char = chars[i]!;
    if (isLatinOrDigit(char)) {
      let j = i + 1;
      while (j < chars.length && isLatinOrDigit(chars[j]!)) j++;
      const runText = chars.slice(i, j).join('');
      units.push({ kind: classifyLatinRun(runText), text: runText, startIndex: i });
      i = j;
    } else {
      units.push({ kind: 'cjk', text: char, startIndex: i });
      i++;
    }
  }
  return units;
}

/** 单元的纵向跨度（未套用压缩前的原始值）。 */
function unitExtent(shaper: Shaper, unit: FlowUnit, fontSize: number): number {
  if (unit.kind === 'cjk' || unit.kind === 'tcy') return fontSize;
  const run = shaper.shape(unit.text, false);
  const scale = fontSize / run.upem;
  return run.glyphs.reduce((sum, g) => sum + g.xAdvance * scale, 0);
}

/**
 * 折行：与 breakLines（layout.ts）同一套禁则思路，泛化到「流单元」而不是
 * 单字符——拉丁串/纵中横块作为整体不被从中间断开。
 */
function breakIntoLines(
  shaper: Shaper,
  units: readonly FlowUnit[],
  fontSize: number,
  maxHeight: number | undefined,
): VerticalLine[] {
  if (units.length === 0) return [];
  const extents = units.map((u) => unitExtent(shaper, u, fontSize));
  if (maxHeight === undefined) {
    return [{ start: 0, end: units.length, extent: extents.reduce((a, b) => a + b, 0) }];
  }

  const lines: VerticalLine[] = [];
  let lineStart = 0;
  let height = 0;
  for (let i = 0; i < units.length; i++) {
    const extent = extents[i]!;
    if (height + extent > maxHeight && i > lineStart) {
      // 禁则：不能在行首禁则字符前断——回退直到合法
      let breakAt = i;
      const canBreak = (idx: number): boolean =>
        idx > lineStart &&
        !KINSOKU_LINE_START.has(units[idx]!.text[0] ?? '') &&
        !KINSOKU_LINE_END.has(units[idx - 1]!.text.at(-1) ?? '');
      while (breakAt > lineStart && !canBreak(breakAt)) breakAt--;

      if (breakAt > lineStart) {
        let lineExtent = 0;
        for (let j = lineStart; j < breakAt; j++) lineExtent += extents[j]!;
        lines.push({ start: lineStart, end: breakAt, extent: lineExtent });
        lineStart = breakAt;
        height = 0;
        for (let j = lineStart; j < i; j++) height += extents[j]!;
        height += extent; // 当前单元也计入新行
      } else {
        // 无处可退：压缩→悬挂二级退让（M3-16 核心规则）
        const remaining = maxHeight - height;
        const resolution = resolveKinsokuOverflow(units[i]!.text[0] ?? '', extent, remaining);
        if (resolution?.kind === 'compress') {
          extents[i] = extent * resolution.factor;
          height += extents[i]!;
          continue; // 压缩后就地收纳，继续本行
        }
        // hang（含无法识别为禁则字符的情形）：收纳本字符，行在此结束
        lines.push({ start: lineStart, end: i + 1, extent: height + extent });
        lineStart = i + 1;
        height = 0;
        continue;
      }
    } else {
      height += extent;
    }
  }
  if (lineStart < units.length) {
    let lineExtent = 0;
    for (let j = lineStart; j < units.length; j++) lineExtent += extents[j]!;
    lines.push({ start: lineStart, end: units.length, extent: lineExtent });
  }
  return lines;
}

export function layoutVertical(
  shaper: Shaper,
  text: string,
  options: VerticalLayoutOptions,
): VerticalLayout {
  const units = toFlowUnits(text);
  const lineHeightPx = options.fontSize * options.lineHeight;
  const lines = breakIntoLines(shaper, units, options.fontSize, options.maxHeight);
  const glyphs: VerticalGlyph[] = [];

  for (const [lineIndex, line] of lines.entries()) {
    // 行右→左：行 0 在最右（x=0），后续行向左排开
    // lineIndex===0 时特判避免 -0（-lineIndex*n 在 lineIndex=0 时产出 -0，
    // 数值上等于 0 但 Object.is 语义下是不同值，容易绊到严格相等断言）
    const lineCenterX = lineIndex === 0 ? 0 : -lineIndex * lineHeightPx;
    let cursor = 0;

    for (let u = line.start; u < line.end; u++) {
      const unit = units[u]!;
      if (unit.kind === 'cjk') {
        const run = shaper.shape(unit.text, true);
        const glyph = run.glyphs[0];
        if (glyph !== undefined) {
          glyphs.push({
            glyphId: glyph.glyphId,
            cluster: unit.startIndex,
            x: lineCenterX,
            y: cursor + options.fontSize * (ASCENT_RATIO - 0.5),
            rotate: 0,
          });
        }
        cursor += options.fontSize;
      } else if (unit.kind === 'tcy') {
        const run = shaper.shape(unit.text, false);
        const scale = options.fontSize / run.upem;
        const totalWidth = run.glyphs.reduce((sum, g) => sum + g.xAdvance * scale, 0);
        let x = -totalWidth / 2;
        for (const g of run.glyphs) {
          glyphs.push({
            glyphId: g.glyphId,
            cluster: unit.startIndex,
            x: lineCenterX + x + (g.xOffset * scale),
            y: cursor + options.fontSize * ASCENT_RATIO,
            rotate: 0,
          });
          x += g.xAdvance * scale;
        }
        cursor += options.fontSize;
      } else {
        // 拉丁/数字串：整体旋转 90°，沿行方向铺开
        const run = shaper.shape(unit.text, false);
        const scale = options.fontSize / run.upem;
        let localX = 0;
        for (const g of run.glyphs) {
          glyphs.push({
            glyphId: g.glyphId,
            cluster: unit.startIndex + g.cluster,
            x: lineCenterX,
            y: cursor + localX,
            rotate: 90,
          });
          localX += g.xAdvance * scale;
        }
        cursor += localX;
      }
    }
  }

  return {
    glyphs,
    lines,
    width: lines.length * lineHeightPx,
    height: Math.max(0, ...lines.map((l) => l.extent)),
  };
}

/**
 * glyph 的顺时针旋转变换矩阵（`[a, b, c, d]`，2×2，用于 Canvas
 * `transform(a, b, c, d, e, f)` 的旋转部分）。0° 恒为单位矩阵。
 *
 * 推广自只服务竖排文字（0°/90° 两种）的早期版本，现在接受任意角度——
 * 文字变形（`engine/text/warp.ts`）每个字形算出的旋转角是连续值，不能再
 * 只认两个离散档。90 的整数倍角度仍然强制取整（`Math.cos(Math.PI/2)` 在
 * IEEE754 下是 6.12e-17 不是精确 0，竖排文字这些年一直依赖这里给出精确
 * 的 0/1，不能因为推广就悄悄引入这个量级的浮点噪声）；非 90 整数倍的角度
 * 没有"精确值"这个概念，原样使用三角函数结果。
 */
export function rotationMatrix(degrees: number): readonly [number, number, number, number] {
  // 0° 早退：下面的通用路径会算出 -sin(0) = -0，`toEqual([1,0,0,1])` 这类
  // 严格比较会把 -0 跟 0 当成不同值——0° 是最常见的档，不值得让调用方自己
  // 记得处理这个坑
  if (degrees === 0) return [1, 0, 0, 1];
  const rad = (degrees * Math.PI) / 180;
  let cos = Math.cos(rad);
  let sin = Math.sin(rad);
  if (Number.isInteger(degrees) && degrees % 90 === 0) {
    cos = Math.round(cos);
    sin = Math.round(sin);
  }
  // Canvas 2D 变换矩阵：[a c; b d]，顺时针旋转（y 向下坐标系）
  return [cos, sin, -sin, cos];
}

export { isCjk };
